import { padNumber } from 'gossamer-unified';

export type EventRepetition = {
  frequency: 'weekly';
  interval_weeks: number;
  weekdays: number[];
  end_date: string;
};

/**
 * Allocates a series at its selected effective occurrence. Active eligible
 * members at or after that occurrence move to the new group; cancelled
 * members always remain historical records in the original group. Individually
 * changed members are protected unless the caller explicitly includes them.
 */
export const getEventSeriesSplitPreview = (
  effectiveFrom: string,
  repetition: EventRepetition,
  selectedEventId: number,
  occurrences: EventRepetitionPreviewOccurrence[],
  includeExceptions = false,
): EventSeriesSplitPreview => {
  const effectiveFromTime = dateTimeKey(effectiveFrom);
  if (Number.isNaN(effectiveFromTime)) throw new Error('effective date must be a valid date');

  const selected = occurrences.find((occurrence) => occurrence.id === selectedEventId);
  if (!selected || dateTimeKey(selected.starts_at) !== effectiveFromTime) {
    throw new Error('effective date must be the selected occurrence start');
  }
  if (selected.is_cancelled) throw new Error('a cancelled occurrence cannot start a new series');
  if (!includeExceptions && isProtectedRepeatException(selected)) {
    throw new Error('the selected occurrence is protected; include exceptions to split from it');
  }

  const expectedStarts = new Set(getWeeklyRepetitionDatesOnOrAfter(effectiveFrom, repetition).map(dateTimeKey));
  if (!expectedStarts.has(effectiveFromTime)) {
    throw new Error('the new pattern must include the selected effective occurrence');
  }
  const existingStarts = new Set(occurrences.map((occurrence) => dateTimeKey(occurrence.starts_at)));
  const items: EventSeriesSplitPreviewItem[] = [];

  for (const occurrence of occurrences) {
    const startsAt = dateTimeKey(occurrence.starts_at);
    if (startsAt < effectiveFromTime) {
      items.push({
        outcome: 'remain-original',
        event_id: occurrence.id,
        starts_at: occurrence.starts_at,
        active_booking_count: occurrence.active_booking_count,
        payment_count: occurrence.payment_count,
        reason: 'before the effective date',
      });
      continue;
    }
    if (occurrence.is_cancelled) {
      items.push({
        outcome: 'excluded-cancelled',
        event_id: occurrence.id,
        starts_at: occurrence.starts_at,
        active_booking_count: occurrence.active_booking_count,
        payment_count: occurrence.payment_count,
        reason: 'cancelled occurrences retain their original-series history',
      });
      continue;
    }
    if (!includeExceptions && isProtectedRepeatException(occurrence)) {
      items.push({
        outcome: 'excluded-exception',
        event_id: occurrence.id,
        starts_at: occurrence.starts_at,
        active_booking_count: occurrence.active_booking_count,
        payment_count: occurrence.payment_count,
        reason: 'individually changed or manually added',
      });
      continue;
    }
    items.push({
      outcome: 'move',
      event_id: occurrence.id,
      starts_at: occurrence.starts_at,
      active_booking_count: occurrence.active_booking_count,
      payment_count: occurrence.payment_count,
    });
  }

  for (const startsAt of getWeeklyRepetitionDatesOnOrAfter(effectiveFrom, repetition)) {
    if (existingStarts.has(dateTimeKey(startsAt))) continue;
    items.push({
      outcome: 'create',
      event_id: null,
      starts_at: startsAt,
      active_booking_count: 0,
      payment_count: 0,
    });
  }

  const summary: EventSeriesSplitPreview['summary'] = {
    'remain-original': 0,
    move: 0,
    create: 0,
    'excluded-exception': 0,
    'excluded-cancelled': 0,
    moved_active_booking_count: 0,
    moved_payment_count: 0,
  };
  for (const item of items) {
    summary[item.outcome] += 1;
    if (item.outcome === 'move') {
      summary.moved_active_booking_count += item.active_booking_count;
      summary.moved_payment_count += item.payment_count;
    }
  }

  return {
    effective_from: effectiveFrom,
    include_exceptions: includeExceptions,
    items: items.sort((first, second) => dateTimeKey(first.starts_at) - dateTimeKey(second.starts_at)),
    summary,
  };
};

export type EventRepeatSource = 'generated' | 'added';
export type EventRepeatExceptionType = 'edited' | 'rescheduled' | 'skipped' | 'cancelled' | 'pattern-exception';

export type EventRepeatMetadata = {
  source: EventRepeatSource;
  exception_types: EventRepeatExceptionType[];
  overridden_fields: string[];
};

export type EventRepeatOccurrenceBadge = 'cancelled' | EventRepeatExceptionType | 'added';

export type EventRepeatGroupSummary = {
  total: number;
  active: number;
  cancelled: number;
  exceptions: number;
};

export type EventRepetitionPreviewScope = 'this-and-future' | 'entire-series';
export type RecurringEventEditScope = EventRepetitionPreviewScope;
export type RecurringEventCancellationScope = 'this-event' | 'this-and-future' | 'entire-series';
export type EventRepetitionPreviewOutcome = 'keep' | 'create' | 'hard-delete' | 'cancel' | 'excluded-exception';

export type EventSeriesSplitPreviewOutcome =
  | 'remain-original'
  | 'move'
  | 'create'
  | 'excluded-exception'
  | 'excluded-cancelled';

export type EventSeriesSplitPreviewItem = {
  outcome: EventSeriesSplitPreviewOutcome;
  event_id: number | null;
  starts_at: string;
  active_booking_count: number;
  payment_count: number;
  reason?: string;
};

export type EventSeriesSplitPreview = {
  effective_from: string;
  include_exceptions: boolean;
  items: EventSeriesSplitPreviewItem[];
  summary: Record<EventSeriesSplitPreviewOutcome, number> & {
    moved_active_booking_count: number;
    moved_payment_count: number;
  };
};

export type EventRepetitionPreviewOccurrence = {
  id: number;
  starts_at: string;
  is_cancelled: boolean;
  is_root: boolean;
  repeat_metadata: EventRepeatMetadata | null;
  active_booking_count: number;
  payment_count: number;
  attendance_count: number;
  protected_dependency_count: number;
};

export type EventRepetitionPreviewItem = {
  outcome: EventRepetitionPreviewOutcome;
  event_id: number | null;
  starts_at: string;
  active_booking_count: number;
  payment_count: number;
  reason?: string;
};

export type EventRepetitionPreview = {
  scope: EventRepetitionPreviewScope;
  effective_from: string;
  include_exceptions: boolean;
  items: EventRepetitionPreviewItem[];
  summary: Record<EventRepetitionPreviewOutcome, number> & {
    affected_active_booking_count: number;
    affected_payment_count: number;
  };
};

export type RecurringEventEditPreviewOccurrence = {
  id: number;
  starts_at: string;
  is_cancelled: boolean;
  repeat_metadata: EventRepeatMetadata | null;
  active_booking_count: number;
  payment_count: number;
};

export type RecurringEventEditPreview = {
  scope: RecurringEventEditScope;
  include_exceptions: boolean;
  affected_event_count: number;
  rescheduling_event_count: number;
  paid_ticket_event_count: number;
  excluded_event_count: number;
  active_booking_count: number;
  payment_count: number;
  excluded: { event_id: number; starts_at: string; reason: string }[];
};

export type RecurringEventDateChange = {
  start_date: string;
  end_date: string;
};

export type RecurringEventCancellationPreview = {
  scope: RecurringEventCancellationScope;
  include_exceptions: boolean;
  total_event_count: number;
  eligible_event_count: number;
  excluded_event_count: number;
  already_cancelled_event_count: number;
  active_booking_count: number;
  payment_count: number;
  excluded: { event_id: number; starts_at: string; reason: string }[];
};

const WEEKDAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

const dateKeyToLocalDate = (dateKey: string): Date | null => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) return null;
  const [year, month, day] = dateKey.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day ? date : null;
};

const localDateKey = (date: Date): string =>
  `${date.getFullYear()}-${padNumber(date.getMonth() + 1, 2)}-${padNumber(date.getDate(), 2)}`;

const parseEventRepetition = (value: unknown): EventRepetition | undefined => {
  if (typeof value === 'string') {
    try {
      return JSON.parse(value) as EventRepetition;
    } catch {
      return undefined;
    }
  }
  return typeof value === 'object' && value !== null ? (value as EventRepetition) : undefined;
};

const parseEventRepeatMetadata = (value: unknown): EventRepeatMetadata | undefined => {
  if (typeof value === 'string') {
    try {
      return JSON.parse(value) as EventRepeatMetadata;
    } catch {
      return undefined;
    }
  }
  return typeof value === 'object' && value !== null ? (value as EventRepeatMetadata) : undefined;
};

export const getEventRepeatMetadata = (value: unknown): EventRepeatMetadata | undefined =>
  parseEventRepeatMetadata(value);

const EVENT_REPEAT_EXCEPTION_TYPES: EventRepeatExceptionType[] = [
  'edited',
  'rescheduled',
  'skipped',
  'cancelled',
  'pattern-exception',
];

export const getEventRepeatOccurrenceBadges = (occurrence: {
  visibility_limit: string;
  repeat_metadata: unknown;
}): EventRepeatOccurrenceBadge[] => {
  const metadata = parseEventRepeatMetadata(occurrence.repeat_metadata);
  const exceptionTypes = Array.isArray(metadata?.exception_types)
    ? metadata.exception_types.filter((type): type is EventRepeatExceptionType =>
        EVENT_REPEAT_EXCEPTION_TYPES.includes(type),
      )
    : [];

  return [
    ...(occurrence.visibility_limit === 'cancelled' ? (['cancelled'] as const) : []),
    ...exceptionTypes.filter((type) => type !== 'cancelled'),
    ...(metadata?.source === 'added' ? (['added'] as const) : []),
  ];
};

export const getEventRepeatGroupSummary = (
  occurrences: Array<{ visibility_limit: string; repeat_metadata: unknown }>,
): EventRepeatGroupSummary =>
  occurrences.reduce<EventRepeatGroupSummary>(
    (summary, occurrence) => {
      const isCancelled = occurrence.visibility_limit === 'cancelled';
      const badges = getEventRepeatOccurrenceBadges(occurrence);
      return {
        total: summary.total + 1,
        active: summary.active + (isCancelled ? 0 : 1),
        cancelled: summary.cancelled + (isCancelled ? 1 : 0),
        exceptions: summary.exceptions + (badges.length > (isCancelled ? 1 : 0) ? 1 : 0),
      };
    },
    { total: 0, active: 0, cancelled: 0, exceptions: 0 },
  );

export const getRepetitionEndBefore = (effectiveFrom: string): string => {
  const effective = new Date(effectiveFrom);
  if (Number.isNaN(effective.getTime())) throw new Error('effective date must be valid');
  effective.setDate(effective.getDate() - 1);
  return localDateKey(effective);
};

export const makeEventRepeatMetadata = (
  value: unknown,
  exceptionType?: EventRepeatExceptionType,
  overriddenFields: string[] = [],
): EventRepeatMetadata => {
  const existing = parseEventRepeatMetadata(value);
  return {
    source: existing?.source || 'generated',
    exception_types: [...new Set([...(existing?.exception_types || []), ...(exceptionType ? [exceptionType] : [])])],
    overridden_fields: [...new Set([...(existing?.overridden_fields || []), ...overriddenFields])],
  };
};

export const formatEventRepetition = (value: unknown): string => {
  const repetition = parseEventRepetition(value);
  if (!repetition || repetition.frequency !== 'weekly' || !Array.isArray(repetition.weekdays)) {
    return typeof value === 'string' ? value : '';
  }

  const interval = repetition.interval_weeks === 1 ? 'Every week' : `Every ${String(repetition.interval_weeks)} weeks`;
  const weekdays = repetition.weekdays
    .map((weekday) => WEEKDAY_LABELS[weekday])
    .filter(Boolean)
    .join(', ');

  return `${interval} on ${weekdays} until ${repetition.end_date}`;
};

const SERIES_RECONCILIATION_OPERATION_LABELS: Record<string, string> = {
  'pattern-apply': 'Repeat pattern updated',
  'series-split': 'Series split',
  'recurring-edit': 'Series details updated',
  'recurring-cancellation': 'Series cancellation',
};

export const formatEventSeriesReconciliationOperation = (value: unknown): string =>
  SERIES_RECONCILIATION_OPERATION_LABELS[String(value)] || 'Series reconciliation';

export const formatEventSeriesReconciliationScope = (value: unknown): string =>
  value === 'this-and-future'
    ? 'This and future events'
    : value === 'entire-series'
      ? 'Entire series'
      : 'Unknown scope';

export const formatEventSeriesReconciliationOutcome = (value: unknown): string => {
  const summary =
    typeof value === 'string'
      ? (() => {
          try {
            return JSON.parse(value) as Record<string, unknown>;
          } catch {
            return {};
          }
        })()
      : value && typeof value === 'object'
        ? (value as Record<string, unknown>)
        : {};
  const count = (key: string): number | null =>
    typeof summary[key] === 'number' && Number.isFinite(summary[key] as number) ? (summary[key] as number) : null;
  const details = [
    ['affected', count('affected_event_count')],
    ['created', count('create')],
    ['cancelled', count('cancel')],
    ['deleted', count('hard-delete')],
    ['excluded', count('excluded_event_count') ?? count('excluded-exception')],
  ].filter((item): item is [string, number] => item[1] !== null);
  return details.length ? details.map(([label, itemCount]) => `${itemCount} ${label}`).join(' · ') : 'Completed';
};

export const getEventRepetitionValidationError = (
  repetition: EventRepetition | undefined,
  startDate?: string,
): string | undefined => {
  if (!repetition) return undefined;
  if (repetition.frequency !== 'weekly') return 'only weekly repetition is supported';
  if (!Number.isInteger(repetition.interval_weeks) || repetition.interval_weeks < 1) {
    return 'repeat interval must be a whole number of weeks';
  }
  if (
    !Array.isArray(repetition.weekdays) ||
    repetition.weekdays.length === 0 ||
    repetition.weekdays.some((weekday) => !Number.isInteger(weekday) || weekday < 0 || weekday > 6)
  ) {
    return 'at least one valid day of the week must be selected';
  }
  if (new Set(repetition.weekdays).size !== repetition.weekdays.length) {
    return 'days of the week must not be duplicated';
  }
  const endDate = dateKeyToLocalDate(repetition.end_date);
  if (!endDate) return 'repeat end date must be a valid date';
  if (!startDate || Number.isNaN(new Date(startDate).getTime()))
    return 'a valid event start date is required for repetition';
  if (repetition.end_date < localDateKey(new Date(startDate))) {
    return 'repeat end date must be on or after the event date';
  }
  return undefined;
};

export const getWeeklyRepetitionDatesOnOrAfter = (startDate: string, repetition: EventRepetition): string[] => {
  const validationError = getEventRepetitionValidationError(repetition, startDate);
  if (validationError) throw new Error(validationError);

  const firstStart = new Date(startDate);
  const until = dateKeyToLocalDate(repetition.end_date)!;
  const anchor = new Date(firstStart.getFullYear(), firstStart.getMonth(), firstStart.getDate());
  const occurrences: string[] = [];

  for (
    let candidateDate = new Date(anchor);
    candidateDate <= until;
    candidateDate.setDate(candidateDate.getDate() + 1)
  ) {
    const candidateDay = Date.UTC(candidateDate.getFullYear(), candidateDate.getMonth(), candidateDate.getDate());
    const anchorDay = Date.UTC(anchor.getFullYear(), anchor.getMonth(), anchor.getDate());
    const weekIndex = Math.floor((candidateDay - anchorDay) / MILLISECONDS_PER_DAY / 7);
    if (weekIndex % repetition.interval_weeks !== 0 || !repetition.weekdays.includes(candidateDate.getDay())) continue;

    const occurrence = new Date(
      candidateDate.getFullYear(),
      candidateDate.getMonth(),
      candidateDate.getDate(),
      firstStart.getHours(),
      firstStart.getMinutes(),
      firstStart.getSeconds(),
      firstStart.getMilliseconds(),
    );
    if (occurrence >= firstStart) occurrences.push(occurrence.toISOString());
  }

  return occurrences;
};

export const getWeeklyRepetitionDates = (startDate: string, repetition: EventRepetition): string[] =>
  getWeeklyRepetitionDatesOnOrAfter(startDate, repetition).filter(
    (occurrence) => new Date(occurrence).getTime() > new Date(startDate).getTime(),
  );

export const isProtectedRepeatException = (occurrence: {
  is_cancelled: boolean;
  repeat_metadata: EventRepeatMetadata | null;
}) => {
  const metadata = occurrence.repeat_metadata;
  return (
    occurrence.is_cancelled ||
    metadata?.source === 'added' ||
    (metadata?.exception_types.length || 0) > 0 ||
    (metadata?.overridden_fields.length || 0) > 0
  );
};

const dateTimeKey = (dateTime: string) => new Date(dateTime).getTime();

/**
 * Applies a selected occurrence's requested time change to another occurrence.
 * A start-time move shifts both boundaries by the same amount. A changed source
 * duration deliberately replaces each occurrence's otherwise independent duration.
 */
export const getRecurringEventDateChange = (
  sourceStartDate: string,
  sourceEndDate: string,
  requestedStartDate: string,
  requestedEndDate: string,
  targetStartDate: string,
  targetEndDate: string,
): RecurringEventDateChange | undefined => {
  const times = [
    sourceStartDate,
    sourceEndDate,
    requestedStartDate,
    requestedEndDate,
    targetStartDate,
    targetEndDate,
  ].map(dateTimeKey);
  if (times.some(Number.isNaN)) throw new Error('event dates must be valid');

  const [sourceStart, sourceEnd, requestedStart, requestedEnd, targetStart, targetEnd] = times;
  if (sourceEnd < sourceStart || requestedEnd < requestedStart || targetEnd < targetStart) {
    throw new Error('event end date must not be before its start date');
  }
  if (sourceStart === requestedStart && sourceEnd === requestedEnd) return undefined;

  const startDelta = requestedStart - sourceStart;
  const requestedDuration = requestedEnd - requestedStart;
  const sourceDuration = sourceEnd - sourceStart;
  const shiftedStart = targetStart + startDelta;
  const shiftedEnd = requestedDuration === sourceDuration ? targetEnd + startDelta : shiftedStart + requestedDuration;
  return {
    start_date: new Date(shiftedStart).toISOString(),
    end_date: new Date(shiftedEnd).toISOString(),
  };
};

export const getEventRepetitionPreview = (
  scope: EventRepetitionPreviewScope,
  effectiveFrom: string,
  repetition: EventRepetition,
  occurrences: EventRepetitionPreviewOccurrence[],
  includeExceptions = false,
): EventRepetitionPreview => {
  const effectiveFromTime = dateTimeKey(effectiveFrom);
  if (Number.isNaN(effectiveFromTime)) throw new Error('effective date must be a valid date');

  const expectedStarts = getWeeklyRepetitionDatesOnOrAfter(effectiveFrom, repetition);
  const occurrencesByStart = new Map(occurrences.map((occurrence) => [dateTimeKey(occurrence.starts_at), occurrence]));
  const items: EventRepetitionPreviewItem[] = [];
  const handledEventIds = new Set<number>();

  for (const startsAt of expectedStarts) {
    const existing = occurrencesByStart.get(dateTimeKey(startsAt));
    if (!existing) {
      items.push({
        outcome: 'create',
        event_id: null,
        starts_at: startsAt,
        active_booking_count: 0,
        payment_count: 0,
      });
      continue;
    }

    handledEventIds.add(existing.id);
    if (!includeExceptions && isProtectedRepeatException(existing)) {
      items.push({
        outcome: 'excluded-exception',
        event_id: existing.id,
        starts_at: existing.starts_at,
        active_booking_count: existing.active_booking_count,
        payment_count: existing.payment_count,
        reason: 'individually changed, cancelled, or manually added',
      });
      continue;
    }

    items.push({
      outcome: 'keep',
      event_id: existing.id,
      starts_at: existing.starts_at,
      active_booking_count: existing.active_booking_count,
      payment_count: existing.payment_count,
    });
  }

  for (const occurrence of occurrences) {
    if (dateTimeKey(occurrence.starts_at) < effectiveFromTime || handledEventIds.has(occurrence.id)) continue;

    if (!includeExceptions && isProtectedRepeatException(occurrence)) {
      items.push({
        outcome: 'excluded-exception',
        event_id: occurrence.id,
        starts_at: occurrence.starts_at,
        active_booking_count: occurrence.active_booking_count,
        payment_count: occurrence.payment_count,
        reason: 'individually changed, cancelled, or manually added',
      });
      continue;
    }

    const canHardDelete =
      occurrence.repeat_metadata?.source === 'generated' &&
      !occurrence.is_root &&
      (occurrence.repeat_metadata.exception_types.length || 0) === 0 &&
      (occurrence.repeat_metadata.overridden_fields.length || 0) === 0 &&
      occurrence.active_booking_count === 0 &&
      occurrence.payment_count === 0 &&
      occurrence.attendance_count === 0 &&
      occurrence.protected_dependency_count === 0;
    items.push({
      outcome: canHardDelete ? 'hard-delete' : 'cancel',
      event_id: occurrence.id,
      starts_at: occurrence.starts_at,
      active_booking_count: occurrence.active_booking_count,
      payment_count: occurrence.payment_count,
      reason: canHardDelete ? undefined : 'has protected history, bookings, payments, attendance, or is the root event',
    });
  }

  const summary: EventRepetitionPreview['summary'] = {
    keep: 0,
    create: 0,
    'hard-delete': 0,
    cancel: 0,
    'excluded-exception': 0,
    affected_active_booking_count: 0,
    affected_payment_count: 0,
  };

  for (const item of items) {
    summary[item.outcome] += 1;
    if (item.outcome === 'cancel' || item.outcome === 'hard-delete') {
      summary.affected_active_booking_count += item.active_booking_count;
      summary.affected_payment_count += item.payment_count;
    }
  }

  return {
    scope,
    effective_from: effectiveFrom,
    include_exceptions: includeExceptions,
    items: items.sort((first, second) => dateTimeKey(first.starts_at) - dateTimeKey(second.starts_at)),
    summary,
  };
};

/**
 * Series-wide ordinary edits deliberately affect only active future occurrences.
 * This avoids changing historical attendee-facing records. The server performs
 * this calculation again under a group lock immediately before applying.
 */
export const getRecurringEventEditPreview = (
  scope: RecurringEventEditScope,
  selectedStartsAt: string,
  occurrences: RecurringEventEditPreviewOccurrence[],
  includeExceptions = false,
  now = new Date().toISOString(),
): RecurringEventEditPreview => {
  const selectedTime = dateTimeKey(selectedStartsAt);
  const nowTime = dateTimeKey(now);
  if (Number.isNaN(selectedTime) || Number.isNaN(nowTime)) throw new Error('event dates must be valid');

  const minimumTime = scope === 'this-and-future' ? selectedTime : nowTime;
  const eligibleOccurrences = occurrences.filter((occurrence) => dateTimeKey(occurrence.starts_at) >= minimumTime);
  const excluded: RecurringEventEditPreview['excluded'] = [];
  let affectedEventCount = 0;
  let activeBookingCount = 0;
  let paymentCount = 0;

  for (const occurrence of eligibleOccurrences) {
    if (occurrence.is_cancelled && !includeExceptions) {
      excluded.push({
        event_id: occurrence.id,
        starts_at: occurrence.starts_at,
        reason: 'cancelled occurrence',
      });
      continue;
    }
    if (!includeExceptions && isProtectedRepeatException(occurrence)) {
      excluded.push({
        event_id: occurrence.id,
        starts_at: occurrence.starts_at,
        reason: 'individually changed, cancelled, or manually added',
      });
      continue;
    }
    affectedEventCount += 1;
    activeBookingCount += occurrence.active_booking_count;
    paymentCount += occurrence.payment_count;
  }

  return {
    scope,
    include_exceptions: includeExceptions,
    affected_event_count: affectedEventCount,
    rescheduling_event_count: 0,
    paid_ticket_event_count: 0,
    excluded_event_count: excluded.length,
    active_booking_count: activeBookingCount,
    payment_count: paymentCount,
    excluded,
  };
};

/**
 * Cancellation always retains the existing single-event cancellation workflow.
 * This only determines which active occurrences may enter that workflow.
 */
export const getRecurringEventCancellationPreview = (
  scope: RecurringEventCancellationScope,
  selectedEventId: number,
  selectedStartsAt: string,
  occurrences: RecurringEventEditPreviewOccurrence[],
  includeExceptions = false,
): RecurringEventCancellationPreview => {
  const selectedTime = dateTimeKey(selectedStartsAt);
  if (Number.isNaN(selectedTime)) throw new Error('selected event date must be valid');

  const excluded: RecurringEventCancellationPreview['excluded'] = [];
  let eligibleEventCount = 0;
  let alreadyCancelledEventCount = 0;
  let activeBookingCount = 0;
  let paymentCount = 0;

  for (const occurrence of occurrences) {
    const isInScope =
      scope === 'this-event'
        ? occurrence.id === selectedEventId
        : scope === 'this-and-future'
          ? dateTimeKey(occurrence.starts_at) >= selectedTime
          : true;
    if (!isInScope) continue;
    if (occurrence.is_cancelled) {
      alreadyCancelledEventCount += 1;
      excluded.push({ event_id: occurrence.id, starts_at: occurrence.starts_at, reason: 'already cancelled' });
      continue;
    }
    if (!includeExceptions && isProtectedRepeatException(occurrence)) {
      excluded.push({
        event_id: occurrence.id,
        starts_at: occurrence.starts_at,
        reason: 'individually changed, rescheduled, or manually added exception',
      });
      continue;
    }
    eligibleEventCount += 1;
    activeBookingCount += occurrence.active_booking_count;
    paymentCount += occurrence.payment_count;
  }

  return {
    scope,
    include_exceptions: includeExceptions,
    total_event_count: occurrences.length,
    eligible_event_count: eligibleEventCount,
    excluded_event_count: excluded.length,
    already_cancelled_event_count: alreadyCancelledEventCount,
    active_booking_count: activeBookingCount,
    payment_count: paymentCount,
    excluded,
  };
};
